// Dependencies
import React from "react"
import { graphql, Link } from "gatsby"
import PropTypes from "prop-types"
// Hooks
// import useMouse from "../hooks/useMouse";
// Components
import Accordian from "../components/Accordian"
import AnnotatedText from "../components/AnnotatedText"
import Layout from "../components/Layout"
import Header from "../components/Header"
import { Image } from "@unpic/react"
import RichText from "../components/RichText"
import Markdown from "../components/Markdown"
import Footer from "../components/Footer"
import NewsletterForm from "../components/NewsletterForm"
import Vimeo from "../components/Vimeo"

export const resolveBlogLink = (category, slug) =>
  category && slug ? `/blog/${category}/${slug}` : "/404"

const CategoryLink = ({ category, activeCategorySlug }) => {
  const isActive = category.slug === activeCategorySlug
  const to = isActive ? "/blog" : `/blog/${category.slug}`
  return (
    <Link
      to={to}
      className="prel p2 tag-button"
      style={
        !activeCategorySlug || isActive
          ? {
              color: category.colours.color,
              backgroundColor: category.colours.primary,
            }
          : {}
      }
      key={category.slug}
    >
      {category.title}
      {isActive && <span className="tag-button-close">X</span>}
    </Link>
  )
}

export const BlogIndexPageComponent = ({
  layout,
  numPages,
  currentPage,
  introduction,
  articles,
  category,
  relatedCategories,
}) => {
  return (
    <Layout {...layout}>
      <Header />
      <div className="container">
        <div className="article-grid">
          <div className="col-4">
            <hgroup>
              <hr className="mbx2 pad-0 hide-border-mobile" />
              <h1 className="h3">{category ? category.title : "Blog"}</h1>
              {introduction && (
                <AnnotatedText
                  className="p2 article-introduction"
                  richText={introduction}
                  // field={section.copy}
                />
              )}
            </hgroup>
          </div>
          <div className="col-2">
            <hr className="mbx2 pad-0 hide-border-mobile" />
            <h3>Categories</h3>
            <div className="flex-wrap gap-1 outerx2" style={{ padding: 0 }}>
              {relatedCategories?.map((entry, index) => (
                <CategoryLink
                  category={entry}
                  activeCategorySlug={category?.slug}
                  key={entry.slug}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="blog-grid outerx2">
          {articles?.map(
            (article, index) =>
              article.slug && (
                <Link
                  to={resolveBlogLink(article.category.slug, article.slug)}
                  className="blog-grid-item"
                  key={article.slug}
                >
                  <Image
                    src={
                      article.coverImage?.file?.url
                        ? `${article.coverImage?.file?.url}?w=640&fm=webp&q=75`
                        : ""
                    }
                  />
                  <p>
                    {article.summary?.text ?? article.title}
                    <br />
                    <span className="opacity-50">
                      {article.category.title} — {article.formattedDate}
                    </span>
                  </p>
                </Link>
              )
          )}
        </div>
      </div>

      <Pagination currentPage={currentPage} numPages={numPages} />
      <Footer />
    </Layout>
  )
}

function Pagination({ currentPage, numPages, categorySlug }) {
  const isFirst = currentPage === 1
  const isLast = currentPage === numPages
  const prefix = categorySlug ? `/blog/${categorySlug}` : "/blog"
  const prevPage =
    currentPage - 1 === 1 ? `${prefix}` : `${prefix}/page/${currentPage - 1}`
  const nextPage = `${prefix}/page/${currentPage + 1}`

  return (
    numPages > 1 && (
      <div className="container p3">
        <div className="advice-info-section">
          <div>
            {!isFirst ? (
              <Link to={prevPage} rel="prev">
                PREVIOUS
              </Link>
            ) : (
              <span className="opacity-30">PREVIOUS</span>
            )}
          </div>
          <div className="has-dark-links flex-center gap-1">
            {Array.from({ length: numPages }, (_, i) => (
              <Link
                key={`pagination-number${i + 1}`}
                className="pad"
                to={i === 0 ? prefix : `${prefix}/page/${i + 1}`}
              >
                {i + 1}
              </Link>
            ))}
          </div>
          <div>
            {!isLast && (
              <Link to={nextPage} rel="next">
                NEXT
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  )
}

const BlogIndex = ({ data, pageContext }) => {
  const { numPages, currentPage } = pageContext
  const defaultIntroduction =
    data.allContentfulSettings.nodes?.[0]?.blogDefaultIntroduction

  const articles = data.allContentfulBlogArticle.nodes
  const introduction = defaultIntroduction

  return (
    <BlogIndexPageComponent
      layout={{
        title: "Blog",
      }}
      numPages={numPages}
      currentPage={currentPage}
      introduction={introduction}
      articles={articles}
      relatedCategories={data.allContentfulBlogCategory.nodes}
    />
  )
}

export default BlogIndex

export const pageQuery = graphql`
  fragment BlogArticleCard on ContentfulBlogArticle {
    title
    slug
    formattedDate: date(formatString: "Do MMMM, YYYY")
    category {
      title
      slug
    }
    coverImage {
      ...Asset
    }
    summary {
      text: summary
    }
  }

  fragment BlogCategoryFragment on ContentfulBlogCategory {
    title
    slug
    colours {
      color
      lightest
      light
      primary
      dark
      darkest
    }
  }

  query BlogIndexQuery($skip: Int!, $limit: Int!) {
    allContentfulSettings(limit: 1) {
      nodes {
        blogDefaultIntroduction {
          raw
        }
      }
    }
    allContentfulBlogArticle(
      skip: $skip
      limit: $limit
      sort: { fields: [date], order: DESC }
      filter: { contentful_id: { ne: "hEkj6bEcFEw6003GbXVBW" } }
    ) {
      nodes {
        ...BlogArticleCard
      }
    }
    allContentfulBlogCategory(sort: { fields: [order], order: ASC }) {
      nodes {
        ...BlogCategoryFragment
      }
    }
  }
`
